import { useClipboardRoute } from "@/features/clipboard/hooks/useClipboardRoute";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

type ClipboardSelectionContextValue = {
  selected: Set<string>;
  selectedFolders: Set<string>;
  toggleSelect: (id: string) => void;
  toggleSelectFolder: (name: string) => void;
  clearSelection: () => void;
  /** Replace the whole selection at once (used by the header select-all). */
  setSelection: (docIds: Iterable<string>, folderNames: Iterable<string>) => void;
};

const ClipboardSelectionContext =
  createContext<ClipboardSelectionContextValue | null>(null);

export const useClipboardSelection = (): ClipboardSelectionContextValue => {
  const value = useContext(ClipboardSelectionContext);
  if (!value) {
    throw new Error(
      "useClipboardSelection must be used within ClipboardSelectionProvider",
    );
  }
  return value;
};

type ClipboardSelectionProviderProps = {
  children: ReactNode;
  /** Cleared alongside selection when the folder/search view changes. */
  onViewChange?: () => void;
};

export const ClipboardSelectionProvider = ({
  children,
  onViewChange,
}: ClipboardSelectionProviderProps) => {
  const { path, routeSearchId, sharedOwnerId } = useClipboardRoute();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectedFolders, setSelectedFolders] = useState<Set<string>>(new Set());

  const clearSelection = useCallback(() => {
    setSelected(new Set());
    setSelectedFolders(new Set());
  }, []);

  const setSelection = useCallback(
    (docIds: Iterable<string>, folderNames: Iterable<string>) => {
      setSelected(new Set(docIds));
      setSelectedFolders(new Set(folderNames));
    },
    [],
  );

  const toggleSelect = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectFolder = useCallback((name: string) => {
    setSelectedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  // Clear selections when the folder / search / shared view changes.
  useEffect(() => {
    clearSelection();
    onViewChange?.();
  }, [path, routeSearchId, sharedOwnerId, clearSelection, onViewChange]);

  const value = useMemo(
    () => ({
      selected,
      selectedFolders,
      toggleSelect,
      toggleSelectFolder,
      clearSelection,
      setSelection,
    }),
    [selected, selectedFolders, toggleSelect, toggleSelectFolder, clearSelection, setSelection],
  );

  return (
    <ClipboardSelectionContext.Provider value={value}>
      {children}
    </ClipboardSelectionContext.Provider>
  );
};
